import { beginWorkspaceHydrationAttempt, onWorkspaceHydrationAttemptStarted, checkpointWorkspaceHydrationAttempt, assertWorkspaceHydrationAttemptCurrent, readStoredWorkspaceHydrationReceipt, WorkspaceHydrationJournalError, type StoredWorkspaceHydrationReceipt } from './workspaceHydrationJournal.js';
import { getAccount } from '@yada/storage';
import { getOrCreateDeviceKey } from '@yada/crypto';
import { getAccountWorkspaceInventory, getDocumentManifest, getDeviceStatus } from '@yada/api-client';

export interface DeviceSyncScope {
  accountId: string;
  profileId: string | null;
  deviceId: string;
}

export interface WorkspaceHydrationReceipt {
  scope: DeviceSyncScope;
  totalWorkspaces: number;
  keyReadyWorkspaces: number;
  syncedWorkspaces: number;
  emptyWorkspaces: number;
  syncedDocuments: number;
  failedWorkspaces: Array<{ workspaceId: string; stage: 'keys' | 'documents' | 'discovery'; message: string }>;
  complete: boolean;
  status: 'running' | 'complete' | 'incomplete';
  finishedAt: number | null;
}

export class WorkspaceHydrationError extends Error {
  constructor(public readonly receipt: WorkspaceHydrationReceipt) {
    super(`Data sync incomplete: ${receipt.syncedWorkspaces} of ${receipt.totalWorkspaces} encrypted workspaces synced; ` +
      `${receipt.failedWorkspaces.length} failed. Existing keys and local changes have been preserved.`);
    this.name = 'WorkspaceHydrationError';
  }
}

function localScope() {
  let accountId: string | null = null;
  try { accountId = JSON.parse(localStorage.getItem('yada:account:account') || 'null')?.id ?? null; } catch { /* Unavailable identity must not match another session. */ }
  return JSON.stringify([accountId, localStorage.getItem('yada:account:activeProfileId'), localStorage.getItem('yada:deviceId')]);
}

export async function captureDeviceSyncScope() {
  const storageScope = localScope();
  const account = await getAccount();
  const key = await getOrCreateDeviceKey();
  if (!account?.id) throw new Error('Sign in before syncing encrypted account data');
  if (localScope() !== storageScope) throw new Error('Account or profile changed during device setup');
  const scope: DeviceSyncScope = { accountId: account.id,
    profileId: localStorage.getItem('yada:account:activeProfileId'), deviceId: key.deviceId };
  return { scope, storageScope, accountless: Boolean(account.isAccountless), async assertCurrent() {
    const currentAccount = await getAccount();
    const currentKey = await getOrCreateDeviceKey();
    if (localScope() !== storageScope || currentAccount?.id !== scope.accountId || currentKey.deviceId !== scope.deviceId) {
      throw new Error('Account, profile or device changed during device setup. Retry in the original account.');
    }
  } };
}

type ScopeCapture = Awaited<ReturnType<typeof captureDeviceSyncScope>>;
const inFlight = new Map<string, Promise<WorkspaceHydrationReceipt>>();
let latest: { storageScope: string; receipt: WorkspaceHydrationReceipt; attemptId: string } | null = null;
let latestFailure: { storageScope: string; message: string } | null = null;
let observesAttempts = false;
const listeners = new Set<(receipt: WorkspaceHydrationReceipt | null) => void>();

export function getWorkspaceHydrationReceipt(): WorkspaceHydrationReceipt | null {
  return latest?.storageScope === localScope() ? latest.receipt : null;
}
/** A safe, scoped transient storage failure, separate from historical receipts. */
export function getWorkspaceHydrationFailure(): string | null {
  return latestFailure?.storageScope === localScope() ? latestFailure.message : null;
}
/** Prior attempt history, never a current verified execution receipt. */
export async function getStoredWorkspaceHydrationReceipt(): Promise<StoredWorkspaceHydrationReceipt | null> {
  const capture = await captureDeviceSyncScope();
  if (capture.accountless) {
    await capture.assertCurrent();
    return null;
  }
  const receipt = await readStoredWorkspaceHydrationReceipt(capture.scope);
  await capture.assertCurrent();
  return receipt;
}
export function onWorkspaceHydrationChange(callback: (receipt: WorkspaceHydrationReceipt | null) => void) {
  listeners.add(callback);
  return () => { listeners.delete(callback); };
}
function invalidate(capture: Pick<ScopeCapture, 'storageScope'>, notifyEmpty = false) {
  if (latest?.storageScope !== capture.storageScope) {
    if (notifyEmpty && localScope() === capture.storageScope) {
      for (const listener of listeners) { try { listener(null); } catch {} }
    }
    return;
  }
  latest = null;
  for (const listener of listeners) { try { listener(null); } catch {} }
}
/** Remove current verification before a fresh check; historical progress and keys are untouched. */
export function invalidateWorkspaceHydrationReceipt(): void {
  const storageScope = localScope();
  const hadFailure = latestFailure?.storageScope === storageScope;
  if (hadFailure) latestFailure = null;
  invalidate({ storageScope }, hadFailure);
}
function publish(capture: ScopeCapture, receipt: WorkspaceHydrationReceipt, attemptId: string) {
  if (localScope() !== capture.storageScope) return;
  const snapshot = freezeReceipt(receipt);
  latest = { storageScope: capture.storageScope, receipt: snapshot, attemptId };
  for (const listener of listeners) { try { listener(snapshot); } catch { /* A view cannot alter execution. */ } }
}
function freezeReceipt(receipt: WorkspaceHydrationReceipt): WorkspaceHydrationReceipt {
  const snapshot = { ...receipt, scope: Object.freeze({ ...receipt.scope }),
    failedWorkspaces: receipt.failedWorkspaces.map(failure => Object.freeze({ ...failure })) };
  Object.freeze(snapshot.failedWorkspaces);
  return Object.freeze(snapshot);
}

/** Coalesce only the original account/profile/device; failure never certifies another scope. */
export async function hydrateAccountWorkspaces(): Promise<WorkspaceHydrationReceipt> {
  const capture = await captureDeviceSyncScope();
  if (!observesAttempts) {
    observesAttempts = true;
    onWorkspaceHydrationAttemptStarted(notice => {
      if (!latest || notice.attemptId === latest.attemptId) return;
      const currentScope = latest.receipt.scope;
      if (notice.scope.accountId !== currentScope.accountId || notice.scope.profileId !== currentScope.profileId || notice.scope.deviceId !== currentScope.deviceId) return;
      latest = null;
      for (const listener of listeners) { try { listener(null); } catch {} }
    });
  }
  const key = JSON.stringify(capture.scope);
  const pending = inFlight.get(key);
  if (pending) return pending;
  if (latestFailure?.storageScope === capture.storageScope) latestFailure = null;
  invalidate(capture, true);
  const task = hydrate(capture).catch(error => {
    if (error instanceof WorkspaceHydrationJournalError && localScope() === capture.storageScope) {
      latestFailure = { storageScope: capture.storageScope, message: error.message };
      invalidate(capture, true);
    }
    throw error;
  }).finally(() => { inFlight.delete(key); });
  inFlight.set(key, task);
  return task;
}

async function hydrate(capture: ScopeCapture): Promise<WorkspaceHydrationReceipt> {
  const receipt: WorkspaceHydrationReceipt = { scope: capture.scope, totalWorkspaces: 0, keyReadyWorkspaces: 0,
    syncedWorkspaces: 0, emptyWorkspaces: 0, syncedDocuments: 0, failedWorkspaces: [],
    complete: false, status: 'running', finishedAt: null };
  let attempt = await beginWorkspaceHydrationAttempt(receipt);
  const assertActive = async () => {
    await capture.assertCurrent();
    await assertWorkspaceHydrationAttemptCurrent(attempt);
    await capture.assertCurrent();
  };
  const persistAndPublish = async () => {
    attempt = await checkpointWorkspaceHydrationAttempt(attempt, receipt);
    publish(capture, receipt, attempt.attemptId);
  };
  publish(capture, receipt, attempt.attemptId);
  try {
    if (capture.accountless) throw new Error('Remote account sync is unavailable in local-only mode');
    await assertActive();
    const approval = await getDeviceStatus(capture.scope.deviceId);
    await assertActive();
    if (approval.status !== 'approved') throw new Error('This device must be approved before resuming account sync');
    await assertActive();
    const inventory = await getAccountWorkspaceInventory();
    await assertActive();
    if (inventory.accountId !== capture.scope.accountId) throw new Error('Workspace inventory belongs to another account');
    const active = inventory.workspaces.filter(workspace => workspace.status === 'active');
    const encrypted = active.filter(workspace => workspace.encrypted);
    receipt.totalWorkspaces = encrypted.length;
    // No key rows does not prove an empty storage node. Verify its manifest
    // without opening documents (which could otherwise create a replacement key).
    for (const workspace of active.filter(workspace => !workspace.encrypted)) {
      await assertActive();
      try {
        const manifest = await getDocumentManifest(workspace.id);
        await assertActive();
        if (manifest.documents.some(document => !document.tombstone)) {
          receipt.totalWorkspaces++;
          receipt.failedWorkspaces.push({ workspaceId: workspace.id, stage: 'keys',
            message: 'Remote encrypted documents exist, but no encryption key was discovered. Use recovery or another trusted device.' });
        } else receipt.emptyWorkspaces++;
      } catch (error) {
        await assertActive();
        receipt.totalWorkspaces++;
        receipt.failedWorkspaces.push({ workspaceId: workspace.id, stage: 'documents',
          message: error instanceof Error ? error.message : 'Could not verify whether this workspace is empty' });
      }
      await persistAndPublish();
    }
    await persistAndPublish();
    const { getWorkspaceSeed } = await import('./workspaceSeedManager.js');
    const { syncDocumentManifest } = await import('./documentSync.js');
    for (const workspace of encrypted) {
      await assertActive();
      try {
        await getWorkspaceSeed(workspace.id, false);
        await assertActive();
        receipt.keyReadyWorkspaces++;
      } catch (error) {
        await assertActive();
        receipt.failedWorkspaces.push({ workspaceId: workspace.id, stage: 'keys',
          message: error instanceof Error ? error.message : 'Encryption keys unavailable' });
        await persistAndPublish();
        continue;
      }
      await persistAndPublish();
      try {
        await assertActive();
        const result = await syncDocumentManifest(workspace.id);
        await assertActive();
        receipt.syncedWorkspaces++;
        receipt.syncedDocuments += result.totalManifestCount;
      } catch (error) {
        await assertActive();
        receipt.failedWorkspaces.push({ workspaceId: workspace.id, stage: 'documents',
          message: error instanceof Error ? error.message : 'Document sync unavailable' });
      }
      await persistAndPublish();
    }
    await assertActive();
    const finalApproval = await getDeviceStatus(capture.scope.deviceId);
    await assertActive();
    if (finalApproval.status !== 'approved') throw new Error('Device approval changed during account sync');
    receipt.complete = receipt.failedWorkspaces.length === 0 && receipt.syncedWorkspaces === receipt.totalWorkspaces;
  } catch (error) {
    if (error instanceof WorkspaceHydrationJournalError) throw error;
    receipt.failedWorkspaces.push({ workspaceId: '', stage: 'discovery',
      message: error instanceof Error ? error.message : 'Account workspace discovery unavailable' });
  }
  receipt.status = receipt.complete ? 'complete' : 'incomplete';
  receipt.finishedAt = Date.now();
  try {
    attempt = await checkpointWorkspaceHydrationAttempt(attempt, receipt);
  } catch (error) {
    if (error instanceof WorkspaceHydrationJournalError && localScope() === capture.storageScope) {
      latestFailure = { storageScope: capture.storageScope, message: error.message };
    }
  } finally {
    publish(capture, receipt, attempt.attemptId);
  }
  const finalReceipt = freezeReceipt(receipt);
  if (!receipt.complete) throw new WorkspaceHydrationError(finalReceipt);
  return finalReceipt;
}
